import { Capacitor } from '@capacitor/core';
import { initializeApp, getApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth as getFirebaseAuth,
  getRedirectResult,
  GoogleAuthProvider,
  inMemoryPersistence,
  initializeAuth,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  setPersistence as firebaseSetPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
  type Auth,
  type User,
} from 'firebase/auth';
import { apiUrl } from '@/config/api';

type FirebaseConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

type NativeFirebaseAuth = typeof import('@capacitor-firebase/authentication').FirebaseAuthentication;

let firebaseConfig: FirebaseConfig | null = null;
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let initPromise: Promise<{ app: FirebaseApp; auth: Auth }> | null = null;

const REDIRECT_MARKER_KEY = 'pendingAuthRedirect';
const REDIRECT_ATTEMPT_KEY = 'authAttempt';
const SIGNED_OUT_KEY = 'yapzap_signed_out';

const getFirebaseConfig = async (): Promise<FirebaseConfig> => {
  if (firebaseConfig) {
    return firebaseConfig;
  }

  firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  return firebaseConfig;
};

const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const isSignedOut = () => localStorage.getItem(SIGNED_OUT_KEY) !== null;

// CRITICAL: Do NOT cache or return the Capacitor plugin proxy from an async function.
// The Capacitor proxy intercepts ALL property access including .then(), which triggers
// JavaScript's thenable detection when the proxy is wrapped in a Promise.
// Instead, always import inline and use immediately — the original working pattern.

const initializeFirebase = async (): Promise<{ app: FirebaseApp; auth: Auth }> => {
  if (firebaseApp && firebaseAuth) {
    return { app: firebaseApp, auth: firebaseAuth };
  }

  const config = await getFirebaseConfig();
  const nativePlatform = isNativePlatform();

  if (getApps().length > 0) {
    firebaseApp = getApp();
  } else {
    firebaseApp = initializeApp(config);
  }

  if (nativePlatform) {
    // On native, use browserLocalPersistence (localStorage) so that email/password auth
    // sessions survive page reloads in the Capacitor WebView. Google/Apple sign-in use
    // the native SDK which handles its own persistence via the iOS keychain.
    // Previously used inMemoryPersistence to avoid IndexedDB hangs, but localStorage
    // doesn't have that issue and email auth requires persistence.
    try {
      firebaseAuth = initializeAuth(firebaseApp, {
        persistence: browserLocalPersistence,
      });
    } catch {
      // initializeAuth throws if already called for this app — get existing instance
      firebaseAuth = getFirebaseAuth(firebaseApp);
    }
  } else {
    // Web: try getAuth first (reuses existing instance), fall back to initializeAuth
    try {
      firebaseAuth = getFirebaseAuth(firebaseApp);
    } catch {
      try {
        firebaseAuth = initializeAuth(firebaseApp, {
          persistence: [browserLocalPersistence, browserSessionPersistence],
          popupRedirectResolver: browserPopupRedirectResolver,
        });
      } catch {
        firebaseAuth = getFirebaseAuth(firebaseApp);
      }
    }
  }

  return { app: firebaseApp, auth: firebaseAuth };
};

const init = async () => {
  if (!initPromise) {
    initPromise = initializeFirebase();
  }

  return initPromise;
};

const extractNativeIdToken = (result: unknown) => {
  if (typeof result === 'string') {
    return result;
  }

  if (result && typeof result === 'object') {
    const tokenResult = result as { token?: string; value?: string };
    return tokenResult.token ?? tokenResult.value ?? null;
  }

  return null;
};

export const toFirebaseCompatibleUser = (nativeUser: any): User | null => {
  if (!nativeUser) {
    return null;
  }

  return {
    uid: nativeUser.uid,
    email: nativeUser.email,
    displayName: nativeUser.displayName || null,
    photoURL: nativeUser.photoURL || null,
    phoneNumber: nativeUser.phoneNumber || null,
    providerId: 'firebase',
    emailVerified: nativeUser.emailVerified || false,
    isAnonymous: false,
    metadata: {
      creationTime: nativeUser.creationTime,
      lastSignInTime: nativeUser.lastSignInTime,
    },
    providerData: nativeUser.providerData || [],
    refreshToken: '',
    tenantId: null,
    delete: async () => {},
    getIdToken: async (forceRefresh?: boolean) => {
      const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
      const result = await nativeAuth.getIdToken({ forceRefresh: !!forceRefresh });
      const token = extractNativeIdToken(result);

      if (!token) {
        throw new Error('No valid token received from native authentication');
      }

      return token;
    },
    getIdTokenResult: async () => ({
      token: '',
      claims: {},
      authTime: '',
      issuedAtTime: '',
      expirationTime: '',
      signInProvider: null,
      signInSecondFactor: null,
    }),
    reload: async () => {},
    toJSON: () => ({ uid: nativeUser.uid, email: nativeUser.email }),
  } as User;
};

class AuthService {
  private googleProvider: GoogleAuthProvider | null = null;
  private appleProvider: OAuthProvider | null = null;

  private getGoogleProvider() {
    if (!this.googleProvider) {
      this.googleProvider = new GoogleAuthProvider();
      this.googleProvider.addScope('email');
      this.googleProvider.addScope('profile');
    }

    return this.googleProvider;
  }

  private getAppleProvider() {
    if (!this.appleProvider) {
      this.appleProvider = new OAuthProvider('apple.com');
      this.appleProvider.addScope('email');
      this.appleProvider.addScope('name');
    }

    return this.appleProvider;
  }

  private clearSignedOutFlag() {
    localStorage.removeItem(SIGNED_OUT_KEY);
  }

  private async saveProfile(token: string, userData?: {
    dateOfBirth?: string;
    marketingEmailConsent?: boolean;
    marketingConsentDate?: string | null;
    termsAcceptedDate?: string;
  }) {
    if (!userData) {
      return;
    }

    await fetch(apiUrl('/api/auth/update-user-profile'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        dateOfBirth: userData.dateOfBirth,
        marketingEmailConsent: userData.marketingEmailConsent || false,
        marketingConsentDate: userData.marketingConsentDate,
        termsAcceptedDate: userData.termsAcceptedDate,
      }),
    });
  }

  private async tryNativeIdToken(forceRefresh = false) {
    if (!isNativePlatform()) {
      return null;
    }

    try {
      const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
      const result = await nativeAuth.getIdToken({ forceRefresh: !!forceRefresh });
      return extractNativeIdToken(result);
    } catch {
      return null;
    }
  }

  async getAuth() {
    const { auth } = await init();
    return auth;
  }

  async signInWithEmail(email: string, password: string) {
    this.clearSignedOutFlag();
    const isNative = isNativePlatform();
    console.log(`[Auth] signInWithEmail called — email=${email}, isNative=${isNative}`);
    // Email/password auth always uses web Firebase SDK — the native Capacitor plugin
    // does not support signInWithEmailAndPassword when skipNativeAuth is false.
    try {
      const auth = await this.getAuth();
      console.log('[Auth] Got auth instance, calling signInWithEmailAndPassword...');
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log('[Auth] Email sign-in succeeded:', result?.user?.email);

      // On native, inMemoryPersistence means the web auth won't survive page reloads.
      // Bridge: get the web user's ID token → exchange for custom token → sign into
      // native Firebase SDK so the native auth state listener picks up the user.
      if (isNative && result?.user) {
        try {
          console.log('[Auth] Bridging email auth to native SDK...');
          const idToken = await result.user.getIdToken();
          const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
          // Use the plugin's web-only signInWithCustomToken via the exchange-token backend
          const { apiUrl } = await import('@/config/api');
          const response = await fetch(apiUrl('/api/auth/exchange-token'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken }),
          });
          if (response.ok) {
            const { customToken } = await response.json();
            // Sign the web auth instance with the custom token to keep it alive
            // The native auth state listener will also pick up the user
            const { signInWithCustomToken: webSignInCustom } = await import('firebase/auth');
            await webSignInCustom(auth, customToken);
            console.log('[Auth] Email auth bridged to native SDK successfully');
          }
        } catch (bridgeErr) {
          console.warn('[Auth] Email auth bridge failed (non-fatal):', bridgeErr);
          // Non-fatal — user is still signed in via web SDK for this session
        }
      }

      return result;
    } catch (err: any) {
      console.error('[Auth] Email sign-in FAILED:', err?.code, err?.message);
      throw err;
    }
  }

  async signUpWithEmail(
    email: string,
    password: string,
    userData?: {
      dateOfBirth?: string;
      marketingEmailConsent?: boolean;
      marketingConsentDate?: string | null;
      termsAcceptedDate?: string;
    }
  ) {
    this.clearSignedOutFlag();
    const isNative = isNativePlatform();
    // Email/password auth always uses web Firebase SDK — the native Capacitor plugin
    // does not support createUserWithEmailAndPassword when skipNativeAuth is false.
    const auth = await this.getAuth();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const token = await userCredential.user.getIdToken();
    await this.saveProfile(token, userData).catch(() => {});

    // Bridge email auth to native SDK (same as signInWithEmail)
    if (isNative && userCredential?.user) {
      try {
        const { apiUrl } = await import('@/config/api');
        const response = await fetch(apiUrl('/api/auth/exchange-token'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: token }),
        });
        if (response.ok) {
          const { customToken } = await response.json();
          const { signInWithCustomToken: webSignInCustom } = await import('firebase/auth');
          await webSignInCustom(auth, customToken);
          console.log('[Auth] Email sign-up bridged to native SDK successfully');
        }
      } catch (bridgeErr) {
        console.warn('[Auth] Email sign-up bridge failed (non-fatal):', bridgeErr);
      }
    }

    return userCredential;
  }

  async signInWithGoogle() {
    this.clearSignedOutFlag();
    const isNative = isNativePlatform();
    console.log(`[Auth] signInWithGoogle called — isNative=${isNative}`);

    if (isNative) {
      console.log('[Auth] Using native Google sign-in...');
      const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
      console.log('[Auth] Got native auth plugin, calling signInWithGoogle()...');
      const result = await Promise.race([
        nativeAuth.signInWithGoogle(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Native Google sign-in timed out after 30s')), 30000)
        ),
      ]);
      console.log('[Auth] Native Google sign-in result:', { hasUser: !!result?.user, hasCredential: !!result?.credential });

      // If native returns user directly, use it
      if (result?.user) {
        console.log('[Auth] Native Google sign-in succeeded with user:', result.user.uid);
        return result.user;
      }

      // Native plugin may return credential but no user (e.g. simulator).
      // Use signInWithCredential on the web SDK to complete the sign-in.
      if (result?.credential?.idToken) {
        console.log('[Auth] Native returned credential without user, bridging via web SDK...');
        const { GoogleAuthProvider: GAP, signInWithCredential: webSignIn } = await import('firebase/auth');
        const auth = await this.getAuth();
        const oauthCredential = GAP.credential(result.credential.idToken, result.credential.accessToken);
        const webResult = await webSignIn(auth, oauthCredential);
        console.log('[Auth] Web credential sign-in succeeded:', webResult.user.email);
        return webResult;
      }

      console.warn('[Auth] Native Google sign-in returned neither user nor credential');
      return null;
    }

    console.log('[Auth] Using web Google sign-in (popup)...');

    const auth = await this.getAuth();
    const provider = this.getGoogleProvider();
    const isMobileReplit =
      window.location.hostname.includes('replit.dev') &&
      (navigator.userAgent.includes('Mobile') || window.innerWidth < 768);

    if (isMobileReplit) {
      provider.setCustomParameters({
        prompt: 'select_account',
        access_type: 'online',
      });

      sessionStorage.setItem(
        REDIRECT_ATTEMPT_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          method: 'google',
          userAgent: navigator.userAgent,
        })
      );
      localStorage.setItem(REDIRECT_MARKER_KEY, 'google');
      await signInWithRedirect(auth, provider);
      return null;
    }

    try {
      const freshProvider = new GoogleAuthProvider();
      freshProvider.addScope('email');
      freshProvider.addScope('profile');
      return await signInWithPopup(auth, freshProvider, browserPopupRedirectResolver);
    } catch (error: any) {
      if (error?.code === 'auth/account-exists-with-different-credential') {
        const { handleAccountExistsError, formatProvidersList } = await import('./accountLinking');
        const linkingInfo = await handleAccountExistsError(error, error.credential);

        if (linkingInfo.needsLinking && linkingInfo.existingProviders) {
          const providersText = formatProvidersList(linkingInfo.existingProviders);
          throw new Error(
            `An account already exists with this email (${linkingInfo.email}). Please sign in with ${providersText} first, then link your Google account in settings.`
          );
        }
      }

      if (
        error?.code === 'auth/popup-blocked' ||
        error?.code === 'auth/unauthorized-domain' ||
        error?.code === 'auth/operation-not-supported-in-this-environment'
      ) {
        localStorage.setItem(REDIRECT_MARKER_KEY, 'google');
        await signInWithRedirect(auth, provider);
        return null;
      }

      throw error;
    }
  }

  async signInWithApple() {
    this.clearSignedOutFlag();

    const isNative = isNativePlatform();
    console.log(`[Auth] signInWithApple called — isNative=${isNative}`);

    if (isNative) {
      console.log('[Auth] Using native Apple sign-in...');
      const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
      console.log('[Auth] Got native auth plugin, calling signInWithApple()...');
      const result = await Promise.race([
        nativeAuth.signInWithApple(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Native Apple sign-in timed out after 30s')), 30000)
        ),
      ]);
      console.log('[Auth] Native Apple sign-in result:', { hasUser: !!result?.user, hasCredential: !!result?.credential });

      if (result?.user) {
        console.log('[Auth] Native Apple sign-in succeeded with user:', result.user.uid);
        return result.user;
      }

      // Native plugin may return credential but no user (e.g. simulator).
      if (result?.credential?.idToken) {
        console.log('[Auth] Native returned credential without user, bridging via web SDK...');
        const { OAuthProvider: OAP, signInWithCredential: webSignIn } = await import('firebase/auth');
        const auth = await this.getAuth();
        const oauthCredential = new OAP('apple.com').credential({
          idToken: result.credential.idToken,
          rawNonce: result.credential.nonce,
        });
        const webResult = await webSignIn(auth, oauthCredential);
        console.log('[Auth] Web credential sign-in succeeded:', webResult.user.email);
        return webResult;
      }

      console.warn('[Auth] Native Apple sign-in returned neither user nor credential');
      return null;
    }

    console.log('[Auth] Using web Apple sign-in (popup)...');
    const auth = await this.getAuth();
    const provider = this.getAppleProvider();

    try {
      const freshProvider = new OAuthProvider('apple.com');
      freshProvider.addScope('email');
      freshProvider.addScope('name');
      return await signInWithPopup(auth, freshProvider, browserPopupRedirectResolver);
    } catch (error: any) {
      if (error?.code === 'auth/account-exists-with-different-credential') {
        const { handleAccountExistsError, formatProvidersList } = await import('./accountLinking');
        const linkingInfo = await handleAccountExistsError(error, error.credential);

        if (linkingInfo.needsLinking && linkingInfo.existingProviders) {
          const providersText = formatProvidersList(linkingInfo.existingProviders);
          throw new Error(
            `An account already exists with this email (${linkingInfo.email}). Please sign in with ${providersText} first, then link your Apple account in settings.`
          );
        }
      }

      if (
        error?.code === 'auth/popup-blocked' ||
        error?.code === 'auth/unauthorized-domain' ||
        error?.code === 'auth/operation-not-supported-in-this-environment'
      ) {
        localStorage.setItem(REDIRECT_MARKER_KEY, 'apple');
        await signInWithRedirect(auth, provider);
        return null;
      }

      throw error;
    }
  }

  async handleRedirectResult() {
    if (isNativePlatform()) {
      return null;
    }

    const authAttempt = sessionStorage.getItem(REDIRECT_ATTEMPT_KEY);
    const pendingRedirect = localStorage.getItem(REDIRECT_MARKER_KEY);

    if (!authAttempt && !pendingRedirect) {
      return null;
    }

    const auth = await this.getAuth();

    try {
      const timeoutPromise = new Promise<null>((_, reject) => {
        setTimeout(() => reject(new Error('Redirect check timeout')), 5000);
      });

      const result = await Promise.race([getRedirectResult(auth), timeoutPromise]).catch((error) => {
        if ((error as Error).message === 'Redirect check timeout') {
          return null;
        }

        throw error;
      });

      sessionStorage.removeItem(REDIRECT_ATTEMPT_KEY);
      localStorage.removeItem(REDIRECT_MARKER_KEY);
      return result;
    } catch (error) {
      sessionStorage.removeItem(REDIRECT_ATTEMPT_KEY);
      localStorage.removeItem(REDIRECT_MARKER_KEY);
      throw error;
    }
  }

  async signOut() {
    if (isNativePlatform()) {
      const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
      await nativeAuth.signOut();
      try {
        const auth = await this.getAuth();
        if (auth.currentUser) {
          await signOut(auth);
        }
      } catch {
        // Ignore web cleanup failures after native sign-out.
      }
      return;
    }

    const auth = await this.getAuth();
    await signOut(auth);
  }

  async getIdToken(forceRefresh = false) {
    const nativeToken = await this.tryNativeIdToken(forceRefresh);
    if (nativeToken) {
      return nativeToken;
    }

    const auth = await this.getAuth();
    let user = auth.currentUser;

    if (!user) {
      user = await this.getCurrentUser();
    }

    if (!user) {
      return null;
    }

    try {
      return await user.getIdToken(forceRefresh);
    } catch {
      return null;
    }
  }

  async onAuthStateChanged(callback: (user: User | null) => void) {
    console.log(`[Auth] onAuthStateChanged setup — isNative=${isNativePlatform()}`);
    try {
      if (isNativePlatform()) {
        try {
          console.log('[Auth] Setting up native auth state listener...');
          const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
          console.log('[Auth] Native plugin imported, adding listener...');
          // Track whether web auth has a user (email sign-in uses web SDK on native)
          let webAuthHasUser = false;

          const removeListener = await nativeAuth.addListener('authStateChange', ({ user }) => {
            console.log('[Auth] Native authStateChange fired:', user?.email || 'null');
            if (isSignedOut()) {
              callback(null);
              return;
            }

            if (user) {
              callback(toFirebaseCompatibleUser(user));
            } else if (!webAuthHasUser) {
              // Only emit null from native if web auth doesn't have a user
              // (email sign-in only updates web SDK, not native)
              callback(null);
            }
          });

          const emitCurrentUser = async () => {
            if (isSignedOut()) {
              callback(null);
              return null;
            }

            try {
              const currentUser = await nativeAuth.getCurrentUser();
              const convertedUser = toFirebaseCompatibleUser(currentUser.user);
              if (convertedUser) {
                callback(convertedUser);
              } else if (!webAuthHasUser) {
                callback(null);
              }
              return convertedUser;
            } catch {
              if (!webAuthHasUser) {
                callback(null);
              }
              return null;
            }
          };

          await emitCurrentUser();

          const pollInterval = window.setInterval(async () => {
            if (isSignedOut()) {
              window.clearInterval(pollInterval);
              return;
            }

            const user = await emitCurrentUser();
            if (user) {
              window.clearInterval(pollInterval);
            }
          }, 2000);

          const pollTimeout = window.setTimeout(() => {
            window.clearInterval(pollInterval);
          }, 30000);

          // Also listen to WEB auth state changes — email/password auth uses the web SDK
          // even on native, so the native listener won't detect those sign-ins.
          const { auth: webAuth } = await init();
          const webUnsubscribe = onAuthStateChanged(webAuth, (webUser) => {
            if (isSignedOut()) return;
            if (webUser) {
              console.log('[Auth] Web auth state changed (native platform):', webUser.email);
              webAuthHasUser = true;
              window.clearInterval(pollInterval);
              callback(webUser);
            } else {
              webAuthHasUser = false;
            }
          });

          return () => {
            window.clearInterval(pollInterval);
            window.clearTimeout(pollTimeout);
            removeListener.remove();
            webUnsubscribe();
          };
        } catch (nativeErr) {
          // Native Firebase auth listener setup failed — fall through to web auth
          // for this call only. Don't permanently disable native auth because
          // sign-in calls may still work even if the listener setup was slow.
          console.warn('[Auth Service] Native auth listener setup failed, using web listener fallback:', nativeErr);
        }
      }

      const { auth } = await init();
      let callbackFired = false;
      const fallbackTimeout = window.setTimeout(() => {
        if (!callbackFired) {
          callback(auth.currentUser);
        }
      }, 3000);

      return onAuthStateChanged(
        auth,
        (user) => {
          callbackFired = true;
          window.clearTimeout(fallbackTimeout);
          callback(user);
        },
        () => {
          callbackFired = true;
          window.clearTimeout(fallbackTimeout);
          callback(null);
        }
      );
    } catch {
      callback(null);
      return () => {};
    }
  }

  async refreshAuthState() {
    if (isNativePlatform()) {
      try {
        const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
        const result = await nativeAuth.getCurrentUser();
        return result.user;
      } catch {
        return null;
      }
    }

    try {
      const auth = await this.getAuth();
      return auth.currentUser;
    } catch {
      return null;
    }
  }

  async getCurrentUser() {
    if (isSignedOut()) {
      return null;
    }

    if (isNativePlatform()) {
      try {
        const { FirebaseAuthentication: nativeAuth } = await import('@capacitor-firebase/authentication');
        const result = await nativeAuth.getCurrentUser();
        return toFirebaseCompatibleUser(result.user);
      } catch {
        return null;
      }
    }

    const auth = await this.getAuth();
    return auth.currentUser;
  }

  async sendPasswordResetEmail(email: string) {
    const auth = await this.getAuth();
    return firebaseSendPasswordResetEmail(auth, email);
  }

  async confirmPasswordReset(oobCode: string, newPassword: string) {
    const auth = await this.getAuth();
    return firebaseConfirmPasswordReset(auth, oobCode, newPassword);
  }

  async verifyPasswordResetCode(oobCode: string) {
    const auth = await this.getAuth();
    return firebaseVerifyPasswordResetCode(auth, oobCode);
  }

  async setPersistence(mode: 'local' | 'session' | 'none') {
    try {
      const auth = await this.getAuth();
      const persistence =
        mode === 'session' || mode === 'none' ? browserSessionPersistence : browserLocalPersistence;

      await Promise.race([
        firebaseSetPersistence(auth, persistence),
        new Promise((_, reject) => {
          window.setTimeout(() => reject(new Error('Persistence timeout')), 3000);
        }),
      ]);
    } catch {
      // Ignore persistence failures and keep the existing Firebase fallback behavior.
    }
  }
}

export const authService = new AuthService();

export const getAuthInstance = async () => {
  const { auth } = await init();
  return auth;
};

export const getAppInstance = async () => {
  const { app } = await init();
  return app;
};
